import { atom, useAtomValue, useSetAtom } from 'jotai';
import differenceWith from 'lodash/differenceWith';
import fromPairs from 'lodash/fromPairs';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import toPairs from 'lodash/toPairs';
import { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react';
import { useAnalytics } from '~/hooks/useAnalytics';
import { type AnalyticsEvents } from '~/services/analytics';
import { AsideContext } from '~/state/aside';
import { createSelectionAtoms, SelectionContext } from '~/state/selection';

export interface TableAnalytics {
  filter: AnalyticsEvents;
  quickFilter?: AnalyticsEvents;
  search: AnalyticsEvents;
}

export interface FiltersScopeValue<TFilters extends object = object> {
  analytics: TableAnalytics;
  defaultValues: TFilters;
}

function readFromStorage<T extends object>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return { ...fallback, ...JSON.parse(raw) } as T;
  } catch {
    return fallback;
  }
}

function persistToStorage(key: string, value: object) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors (e.g. quota exceeded)
  }
}

export function patchStoredFilters<T extends object>(storageKey: string, patch: Partial<T>) {
  try {
    const raw = localStorage.getItem(storageKey);
    const current = raw ? (JSON.parse(raw) as Partial<T>) : {};
    persistToStorage(storageKey, { ...current, ...patch });
  } catch {
    // Ignore storage errors (e.g. quota exceeded)
  }
}

function createFilterAtoms(defaultValues: object, storageKey?: string, initialValues?: object) {
  const seed = initialValues ?? (storageKey ? readFromStorage(storageKey, defaultValues) : defaultValues);
  const baseAtom = atom<object>(seed);
  const selection = createSelectionAtoms();

  const filtersAtom = atom(
    (get) => get(baseAtom),
    (_get, set, value: object) => {
      set(baseAtom, value);
      if (storageKey) {
        persistToStorage(storageKey, value);
      }
    },
  );

  const setFiltersAtom = atom(null, (_get, set, filters: object) => {
    set(filtersAtom, filters);
    set(selection.resetSelectionAtom);
  });

  const setFilterAtom = atom(null, (get, set, key: PropertyKey, value: unknown) => {
    const current = get(baseAtom);
    set(setFiltersAtom, { ...current, [key]: value });
  });

  const setFiltersPreserveSelectionAtom = atom(null, (_get, set, filters: object) => {
    set(filtersAtom, filters);
  });

  const setFilterPreserveSelectionAtom = atom(null, (get, set, key: PropertyKey, value: unknown) => {
    const current = get(baseAtom);
    set(filtersAtom, { ...current, [key]: value });
  });

  const setQuickFilterAtom = atom(null, (get, set, filters: object) => {
    set(filtersAtom, { ...defaultValues, ...filters });
    set(selection.resetSelectionAtom);
  });

  const resetFiltersAtom = atom(null, (_get, set) => {
    set(filtersAtom, defaultValues);
    set(selection.resetSelectionAtom);
  });

  const resetFilterAtom = atom(null, (get, set, key: PropertyKey) => {
    const current = get(baseAtom);
    set(filtersAtom, { ...current, [key]: (defaultValues as Record<PropertyKey, unknown>)[key] });
    set(selection.resetSelectionAtom);
  });

  const isDirtyAtom = atom((get) => {
    const filters = get(baseAtom);
    return !isEqual(filters, defaultValues);
  });

  const dirtyStateAtom = atom((get) => {
    const filters = get(baseAtom);
    return fromPairs(differenceWith(toPairs(filters), toPairs(defaultValues), isEqual));
  });

  const quickFilterKeyAtom = atom<string | null>(null);

  return {
    dirtyStateAtom,
    filtersAtom,
    isDirtyAtom,
    quickFilterKeyAtom,
    resetFilterAtom,
    resetFiltersAtom,
    selection,
    setFilterAtom,
    setFilterPreserveSelectionAtom,
    setFiltersAtom,
    setFiltersPreserveSelectionAtom,
    setQuickFilterAtom,
  };
}

type FilterAtoms = ReturnType<typeof createFilterAtoms>;

interface FiltersContextValue {
  analytics: TableAnalytics;
  atoms: FilterAtoms;
}

const TableFiltersContext = createContext<FiltersContextValue | null>(null);

function useFiltersContext() {
  const ctx = useContext(TableFiltersContext);
  if (!ctx) throw new Error('Filter hooks must be used inside <TableFiltersProvider>');
  return ctx;
}

interface TableFiltersProviderProps<TFilters extends object> {
  analytics: TableAnalytics;
  defaultValues: TFilters;
  initialValues?: TFilters;
  storageKey?: string;
}

export const TableFiltersProvider = <TFilters extends object>({
  analytics,
  children,
  defaultValues,
  initialValues,
  storageKey,
}: PropsWithChildren<TableFiltersProviderProps<TFilters>>) => {
  const atomsRef = useRef<FilterAtoms | null>(null);
  if (!atomsRef.current) {
    atomsRef.current = createFilterAtoms(defaultValues, storageKey, initialValues);
  }
  const atoms = atomsRef.current;
  const filtersValue = useMemo(() => ({ analytics, atoms }), [analytics, atoms]);
  const selectionValue = useMemo(() => ({ atoms: atoms.selection }), [atoms]);
  const asideValue = useMemo(() => ({ atoms: atoms.selection.aside }), [atoms]);

  return (
    <AsideContext.Provider value={asideValue}>
      <SelectionContext.Provider value={selectionValue}>
        <TableFiltersContext.Provider value={filtersValue}>{children}</TableFiltersContext.Provider>
      </SelectionContext.Provider>
    </AsideContext.Provider>
  );
};

export const useFilterState = <TFilters extends object>() => {
  const { atoms } = useFiltersContext();
  return useAtomValue(atoms.filtersAtom) as TFilters;
};

export const useSetFilters = <TFilters extends object>() => {
  const { atoms } = useFiltersContext();
  return useSetAtom(atoms.setFiltersAtom) as (filters: TFilters) => void;
};

export const useUpdateFilter = <TFilters extends object>(preserveSelection?: boolean) => {
  const { track } = useAnalytics();
  const { analytics, atoms } = useFiltersContext();
  const setFilter = useSetAtom(preserveSelection ? atoms.setFilterPreserveSelectionAtom : atoms.setFilterAtom);
  const tabName = useAtomValue(atoms.quickFilterKeyAtom);

  return useCallback(
    <K extends keyof TFilters>(
      key: K,
      value: TFilters[K],
      options?: { analyticsProperties?: Record<string, unknown>; disableAnalytics?: boolean },
    ) => {
      setFilter(key, value);

      if (options?.disableAnalytics) return;

      if (key === 'search') {
        track(analytics.search, { tabName, ...options?.analyticsProperties });
      } else {
        track(analytics.filter, {
          filterName: key,
          filterValue: value,
          tabName,
          ...options?.analyticsProperties,
        });
      }
    },
    [setFilter, track, analytics.search, analytics.filter, tabName],
  );
};

export const useSetQuickFilter = <TFilters extends object>() => {
  const { track } = useAnalytics();
  const { analytics, atoms } = useFiltersContext();
  const setQuickFilter = useSetAtom(atoms.setQuickFilterAtom);

  return useCallback(
    (filters: Partial<TFilters>) => {
      setQuickFilter(filters);
      if (analytics.quickFilter) {
        track(analytics.quickFilter, { filters });
      }
    },
    [setQuickFilter, analytics.quickFilter, track],
  );
};

export const useResetFilters = () => {
  const { atoms } = useFiltersContext();
  return useSetAtom(atoms.resetFiltersAtom);
};

export const useResetFilter = <TFilters extends object>() => {
  const { atoms } = useFiltersContext();
  const resetFilter = useSetAtom(atoms.resetFilterAtom);
  return useCallback(<K extends keyof TFilters>(key: K) => resetFilter(key as never), [resetFilter]);
};

export const useSetQuickFilterKey = () => {
  const { atoms } = useFiltersContext();
  const setKey = useSetAtom(atoms.quickFilterKeyAtom);
  return useCallback((key?: string) => setKey(key ?? null), [setKey]);
};

export const useIsDirty = () => {
  const { atoms } = useFiltersContext();
  return useAtomValue(atoms.isDirtyAtom);
};

export const useDirtyState = <TFilters extends object>(ignoreKeys: (keyof TFilters)[] = []): Partial<TFilters> => {
  const { atoms } = useFiltersContext();
  const dirty = useAtomValue(atoms.dirtyStateAtom);
  return omit(dirty, ignoreKeys) as Partial<TFilters>;
};
